import React, { useEffect, useMemo, useState } from 'react';
import {
    API_ADD_LOAN_REPAYMENT_MULTIPLE,
    API_BRANCHES,
    API_CENTERS,
    API_GET_CLIENTS_FOR_REPAYMENT,
    API_REPAYMENT_STAFF
} from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';
import { confirmAction } from '../utils/confirmAction';

const LoanRepaymentList = () => {
    const defaultRepaymentDate = new Date().toISOString().split('T')[0];
    const [formData, setFormData] = useState({
        branch_id: "",
        center_id: "",
        installment_amt: "",
        repayment_paid_by_staff: "",
        selectedClients: [],
        is_closure: 0,
        created_at: defaultRepaymentDate
    });
    const [centerList, setCenterList] = useState([]);
    const [clientsForRepayment, setClientsForRepayment] = useState([]);
    const [branches, setBranches] = useState([]);
    const [employeeList, setEmployeeList] = useState([]);
    const numbers = Array.from({ length: 26 }, (_, index) => index + 1);
    const token = localStorage.getItem('token');
    const { logout } = useAuth();

    const staffEmployees = employeeList;

    const selectedInstallmentTotal = useMemo(() => (
        clientsForRepayment
            .filter((client) => formData.selectedClients.includes(client.id))
            .reduce((total, client) => total + Number(client.is_closure ? client.remaining_principal_amt : client.cycle_installment_amt || 0), 0)
    ), [clientsForRepayment, formData.selectedClients]);

    useEffect(() => {
        const loadMasterData = async () => {
            try {
                const branchResponse = await fetch(API_BRANCHES, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({}),
                });
                if (branchResponse.status === 401) {
                    logout();
                    return;
                }
                const branchData = await branchResponse.json();
                setBranches(branchData.data || []);

                const employeeResponse = await fetch(API_REPAYMENT_STAFF, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({}),
                });
                if (employeeResponse.status === 401) {
                    logout();
                    return;
                }
                const employeeData = await employeeResponse.json();
                setEmployeeList(employeeData.data || []);
            } catch (error) {
                setBranches([]);
                setEmployeeList([]);
            }
        };

        void loadMasterData();
    }, [logout, token]);

    useEffect(() => {
        const loadClientsForRepayment = async () => {
            if (!formData.branch_id || !formData.center_id) {
                setClientsForRepayment([]);
                return;
            }

            try {
                const response = await fetch(API_GET_CLIENTS_FOR_REPAYMENT, {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        branch_id: formData.branch_id,
                        center_id: formData.center_id,
                    }),
                });
                if (response.status === 401) {
                    logout();
                    return;
                }
                const result = await response.json();
                setClientsForRepayment(result?.data || []);
            } catch (error) {
                alert("An error occurred. Please try again.");
            }
        };

        void loadClientsForRepayment();
    }, [formData.branch_id, formData.center_id, logout, token]);

    const getEmployees = async (branchId = '') => {
        try {
            const response = await fetch(API_REPAYMENT_STAFF, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(branchId ? { branch_id: branchId } : {}),
            });
            if (response.status === 401) {
                logout();
                return;
            }
            const responseData = await response.json();
            setEmployeeList(responseData.data || []);
        } catch (error) {
            setEmployeeList([]);
        }
    };

    const getCentersByBranch = async (branchId) => {
        try {
            const response = await fetch(API_CENTERS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ is_all: false, branch_id: branchId })
            });
            if (response.status === 401) {
                logout();
                return;
            }
            const responseData = await response.json();
            setCenterList(responseData.data || []);
        } catch (error) {
            setCenterList([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'branch_id') {
            if (!value) {
                setCenterList([]);
                setClientsForRepayment([]);
                setEmployeeList([]);
                setFormData((prevData) => ({
                    ...prevData,
                    branch_id: "",
                    center_id: "",
                    repayment_paid_by_staff: "",
                    selectedClients: [],
                }));
                return;
            }

            void getCentersByBranch(value);
            void getEmployees(value);
            setClientsForRepayment([]);
            setFormData((prevData) => ({
                ...prevData,
                branch_id: value,
                center_id: "",
                repayment_paid_by_staff: "",
                selectedClients: [],
            }));
            return;
        }

        if (name === 'center_id' && value === "") {
            setClientsForRepayment([]);
            setFormData((prevData) => ({
                ...prevData,
                center_id: "",
                selectedClients: [],
            }));
            return;
        }

        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleCheckboxChange = (clientId) => {
        setFormData((prevState) => {
            const selectedClients = prevState.selectedClients.includes(clientId)
                ? prevState.selectedClients.filter((id) => id !== clientId)
                : [...prevState.selectedClients, clientId];

            return { ...prevState, selectedClients };
        });
    };

    const handleSelectAllChange = () => {
        if (clientsForRepayment.length === 0) {
            return;
        }

        if (formData.selectedClients.length === clientsForRepayment.length) {
            setFormData((prevData) => ({ ...prevData, selectedClients: [] }));
            return;
        }

        const allClientIds = clientsForRepayment.map((client) => client.id);
        setFormData((prevData) => ({ ...prevData, selectedClients: allClientIds }));
    };

    const handleInstallmentChange = (clientId, value) => {
        setClientsForRepayment((prevClients) =>
            prevClients.map((client) =>
                client.id === clientId
                    ? { ...client, last_installment_no: value - 1, is_closure: Number(value) === 26 ? 1 : client.is_closure }
                    : client
            )
        );
    };

    const handleSettlementChange = (clientId, checked) => {
        setClientsForRepayment((prevClients) => (
            prevClients.map((client) => (
                client.id === clientId ? { ...client, is_closure: checked ? 1 : 0 } : client
            ))
        ));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const selectedClientsData = clientsForRepayment
            .filter((client) => formData.selectedClients.includes(client.id))
            .map((client) => {
                const lastInstallmentNo = client.last_installment_no + 1;
                return {
                    ...client,
                    client_id: client.id,
                    installment_no: lastInstallmentNo,
                    last_installment_no: lastInstallmentNo,
                    is_closure: client.is_closure || lastInstallmentNo === 26 ? 1 : 0,
                };
            });

        if (selectedClientsData.length === 0) {
            alert("Please select at least one client for repayment.");
            return;
        }
        if (!confirmAction(`submit repayment for ${selectedClientsData.length} client(s)`)) return;

        try {
            const response = await fetch(API_ADD_LOAN_REPAYMENT_MULTIPLE, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    branch_id: formData.branch_id,
                    center_id: formData.center_id,
                    repayment_paid_by_staff: formData.repayment_paid_by_staff,
                    clients: selectedClientsData,
                    created_at: formData.created_at
                }),
            });
            if (response.status === 401) {
                logout();
                return;
            }
            const result = await response.json();
            if (response.ok && result?.status) {
                alert(result.message);
                setFormData((prevData) => ({
                    ...prevData,
                    repayment_paid_by_staff: '',
                    selectedClients: [],
                    created_at: defaultRepaymentDate,
                }));
            } else {
                alert(result?.message || 'Failed to submit repayment request.');
            }
        } catch (error) {
            alert("An error occurred. Please try again.");
        }
    };

    return (
        <div className="container my-5">
            <h2 className="text-center">Loan Repayment Form</h2>
            <form onSubmit={handleSubmit} className="shadow p-4 rounded bg-light">
                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="branch_id" className="form-label">Branch Name</label>
                            <select
                                className="form-control"
                                name="branch_id"
                                value={formData.branch_id}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Branch</option>
                                {branches.map((branch) => (
                                    <option key={branch.id} value={branch.id}>
                                        {branch.branch_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="center_id" className="form-label">Center Name</label>
                            <select
                                className="form-control"
                                name="center_id"
                                value={formData.center_id}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Center</option>
                                {centerList.map((center) => (
                                    <option key={center.id} value={center.id}>
                                        {center.center_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="table-responsive data-table-shell">
                    <table className="table data-table">
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={
                                            Array.isArray(clientsForRepayment) &&
                                            clientsForRepayment.length > 0 &&
                                            formData.selectedClients.length === clientsForRepayment.length
                                        }
                                        onChange={handleSelectAllChange}
                                    />
                                </th>
                                <th>Client Name</th>
                                <th>Installment Amount</th>
                                <th>Installment No (1-26)</th>
                                <th>Full Settlement</th>
                                <th>Repayment Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clientsForRepayment.length > 0 && clientsForRepayment.map((client) => (
                                <tr key={client.id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={formData.selectedClients.includes(client.id)}
                                            onChange={() => handleCheckboxChange(client.id)}
                                        />
                                    </td>
                                    <td>{client.client_name}</td>
                                    <td>{Number(client.is_closure ? client.remaining_principal_amt : client.cycle_installment_amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td>
                                        {Number(client.is_closure) === 1 ? (
                                            <span className="text-success small">Assigned automatically</span>
                                        ) : (
                                            <select
                                                className="form-control"
                                                value={client.last_installment_no + 1}
                                                onChange={(e) => handleInstallmentChange(client.id, e.target.value)}
                                                required
                                            >
                                                {numbers.map((number) => (
                                                    <option key={number} value={number}>
                                                        {number}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </td>
                                    <td>
                                        <label className="d-flex align-items-center gap-2 mb-0">
                                            <input
                                                type="checkbox"
                                                checked={Number(client.is_closure) === 1}
                                                onChange={(event) => handleSettlementChange(client.id, event.target.checked)}
                                            />
                                            <span>{Number(client.is_closure) === 1 ? 'Yes' : 'No'}</span>
                                        </label>
                                    </td>
                                    <td>
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="created_at"
                                            value={formData.created_at}
                                            onChange={handleChange}
                                            required
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex justify-content-end mt-3 mb-4">
                    <div className="border rounded px-4 py-3 bg-white text-end">
                        <span className="d-block text-muted small">Selected installment total</span>
                        <strong className="fs-5">Rs. {selectedInstallmentTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="repayment_paid_by_staff" className="form-label">Repayment Paid by Staff</label>
                            <select
                                className="form-control"
                                name="repayment_paid_by_staff"
                                value={formData.repayment_paid_by_staff}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Staff</option>
                                {staffEmployees.map((employee) => (
                                    <option key={employee.id} value={employee.id}>
                                        {employee.emp_generate_id} - {employee.emp_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="text-center">
                    <button type="submit" className="btn btn-primary">Submit Selected Repayments</button>
                </div>
            </form>
        </div>
    );
};

export default LoanRepaymentList;
